import React, { useState, useEffect } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import {
  Bars3Icon,
  XMarkIcon,
  ChevronDownIcon,
} from "@heroicons/react/24/outline";
import {
  FaPhoneAlt,
  FaEnvelope,
  FaSearch,
  FaTimes,
  FaRegCalendarAlt,
  FaSignInAlt,
  FaUser,
} from "react-icons/fa";
import { HiOutlineShoppingCart } from "react-icons/hi";
import { Disclosure } from "@headlessui/react";
import { useCart } from "react-use-cart";
import logo from "../assets/images-webp/logo3.webp";
import { Link, useLocation } from "react-router-dom";
import { useNavigate } from "react-router-dom";
import { useCartCount } from "./useCartCount";
import { getCookie } from "../utils/cookies";
import axios from "axios";
import toast, { Toaster } from "react-hot-toast";
import { EcomAPI, CMSAPI } from "../config";

// Navbar pagination
const navigation = [
  { name: "Home", href: "/", current: true },
  {
    name: "About Us",
    href: "/about",
    current: false,
    subItems: [
      { name: "The Story of Waste", href: "/story-of-waste" },
      { name: "Vision & Mission", href: "/vision-mission" },
    ],
  },
  {
    name: "Products",
    href: "/products",
    current: false,
    subItems: [
      { name: "Door to Door Collection", href: "/door-to-door-collection" },
      { name: "Weighbridge", href: "/weighbridge" },
      { name: "Bin Monitoring", href: "/bin-monitoring" },
      { name: "Vehicle Monitoring", href: "/vehicle-monitoring" },
      { name: "Employee Management", href: "/employee-management" },
      { name: "AI Waste Management", href: "/ai-waste-management" },
    ],
  },
  {
    name: "Platform",
    href: "/platform",
    current: false,
    subItems: [{ name: "SwachhAJV", href: "/swachhajv" }],
  },
  {
    name: "Resources",
    href: "/resources",
    current: false,
    subItems: [
      // { name: "Case Studies", href: "/case-studies" },
      { name: "Datasheet", href: "/datasheet" },
      { name: "Videos", href: "/videos" },
      { name: "Brochure", href: "/brochure" },
      { name: "Gallery", href: "/gallery" },
      { name: "Blog", href: "/blogs" },
      // { name: "Podcast", href: "/podcast" },
      { name: "SwachhAJV Shop", href: "/shop" },
      // { name: "Order Summary", href: "/order-summary" },
    ],
  },

  { name: "Contact Us", href: "/contact", current: false },
];
const cartNavigation = [{ name: "Cart", href: "/cart", current: false }];

const profileNavigation = {
  name: "Profile",
  href: "/profile",
  current: false,
  subItems: [
    // { name: "Orders", href: "/orders" },
    // { name: "Register", href: "/register" },
    { name: "Order Summary", href: "/order-summary" },
  ],
};

function classNames(...classes) {
  return classes.filter(Boolean).join(" ");
}

const DemoModal = ({ show, onClose }) => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    mobileNo: "",
    date: "",
    features: {
      doorToDoor: true,
      binMonitoring: true,
      weighbridge: true,
      vehicleTracking: true,
      grievance: true,
      analytics: true,
      customModules: true
    }
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Create a dedicated toast container for this modal
  const [toastContainer] = useState(() => {
    const div = document.createElement("div");
    div.className = "fixed top-4 left-0 right-0 flex justify-center z-[100000]";
    return div;
  });

  useEffect(() => {
    if (show) {
      document.body.appendChild(toastContainer);
    }
    return () => {
      if (document.body.contains(toastContainer)) {
        document.body.removeChild(toastContainer);
      }
    };
  }, [show, toastContainer]);

  const showToast = (message, type = "success") => {
    const toastEl = document.createElement("div");

    // Add these critical styles
    toastEl.style.position = "fixed";
    toastEl.style.top = "20px";
    toastEl.style.left = "50%";
    toastEl.style.transform = "translateX(-50%)";
    toastEl.style.zIndex = "100000";
    toastEl.style.minWidth = "300px";
    toastEl.style.textAlign = "center";
    toastEl.style.pointerEvents = "auto"; // Make sure it's clickable

    // Animation styles
    toastEl.style.opacity = "0";
    toastEl.style.transition = "opacity 0.3s ease-in-out";

    // Content styles
    toastEl.className = `px-6 py-3 rounded-md shadow-lg text-white font-medium ${
      type === "success"
        ? "bg-green-500"
        : type === "warning"
        ? "bg-yellow-500"
        : "bg-green-500"
    }`;
    toastEl.textContent = message;

    document.body.appendChild(toastEl); // Append directly to body

    // Force reflow and animate in
    setTimeout(() => {
      toastEl.style.opacity = "1";
    }, 10);

    const removeToast = () => {
      toastEl.style.opacity = "0";
      setTimeout(() => {
        if (document.body.contains(toastEl)) {
          document.body.removeChild(toastEl);
        }
      }, 300);
    };

    setTimeout(removeToast, 5000);

    // Optional: Click to dismiss
    toastEl.addEventListener("click", removeToast);
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    if (type === "checkbox") {
      setFormData(prev => ({
        ...prev,
        features: {
          ...prev.features,
          [name]: checked
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value,
      }));
    }
  };

  const formatDemoDate = (dateTimeString) => {
    if (!dateTimeString) return "Not specified";
    
    const date = new Date(dateTimeString);
    
    const options = {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      timeZoneName: 'short'
    };
    
    return date.toLocaleDateString('en-US', options);
  };

  const getSelectedFeatures = () => {
    const selected = [];
    if (formData.features.doorToDoor) selected.push("Door-to-Door Collection");
    if (formData.features.binMonitoring) selected.push("Bin Monitoring");
    if (formData.features.weighbridge) selected.push("Weighbridge Integration");
    if (formData.features.vehicleTracking) selected.push("Vehicle Tracking");
    if (formData.features.grievance) selected.push("Grievance Management");
    if (formData.features.analytics) selected.push("Analytics & Dashboards");
    if (formData.features.customModules) selected.push("Custom Modules");
    return selected;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    showToast("Processing your demo request...", "info");

    try {
      const formattedDate = formatDemoDate(formData.date);
      const selectedFeatures = getSelectedFeatures();

      // Prepare email content for both emails
      const adminEmailBody = `
<h2>📅 NEW DEMO REQUEST FOR AI ENABLED WASTE SOLUTIONS</h2>
─────────────────</br>

<h3>👤 CLIENT DETAILS</h3></br>
<p>• Name: ${formData.name} </p> </br>
<p>• Email: ${formData.email}</p> </br>
<p>• Mobile: ${formData.mobileNo}</p></br>
<p>• Demo Date: ${formattedDate}</p></br>
<h4>🌟 REQUESTED FEATURES</h4>
────────────────────</br>
${selectedFeatures.map(feature => `<p>✔ ${feature}</p></br>`).join('')}
`;

      const thankYouBody = `
<h3>Dear ${formData.name},</h3></br>
<p>Thank you for booking a demo of our AI Enabled Waste Solutions!<p></br>

<h3>📅 Your Demo Details:</h3></br>
<p>- Date: ${formattedDate}</p></br>
<p>- Contact Email: ${formData.email}</p></br>
<p>- Mobile: ${formData.mobileNo}</p></br>
<p>- Features: ${selectedFeatures.join(', ')}</p></br>

<p>We'll contact you shortly to confirm your demo session. Meanwhile, explore our features at https://ajeevi.com/.<p></br>

<p>Need immediate assistance?</p></br>
<p>📞 Call us at +91-9654323500</p></br>
<p>📧 Email support@ajeevi.com</p></br>

<p>Best regards,</p></br>
<p>The Smart Waste Team(SwachhAJV)</p></br>
`;

      // Send both requests in parallel
      const [adminResponse, userResponse] = await Promise.all([
        axios.post(`${CMSAPI}/api/Website/send-mail`, null, {
          params: {
            name: formData.name,
            customeremail: "presales@ajeevi.com",
            subject: "New Demo Booking Request",
            body: adminEmailBody,
          },
        }),
        axios.post(`${CMSAPI}/api/Website/send-mail`, null, {
          params: {
            name: formData.name,
            customeremail: formData.email,
            subject: "Thank You for Booking a Demo!",
            body: thankYouBody,
          },
        }).catch(err => {
          console.error("Thank-you email failed:", err);
          return { data: "Failed" }; // Don't throw, we'll handle gracefully
        })
      ]);

      // Check responses
      if (!adminResponse.data.includes("OK")) {
        throw new Error(adminResponse.data?.message || "Failed to book demo");
      }

      // Show success message immediately
      showToast(
        "Demo booked successfully! " + 
        (userResponse.data.includes("OK") 
          ? "Check your email for confirmation." 
          : "Confirmation email may be delayed."),
        userResponse.data.includes("OK") ? "success" : "warning"
      );

      onClose();
    } catch (error) {
      console.error("Error booking demo:", error);
      showToast(
        error.message || "Failed to book demo. Please try again.",
        "error"
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!show) return null;

  return (
    <>
      <Toaster
        position="top-center"
        containerStyle={{
          top: "4rem",
          zIndex: 99999,
        }}
        toastOptions={{
          style: {
            zIndex: 99999,
          },
        }}
      />
      <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black bg-opacity-50">
        <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl mx-4">
          <div className="flex justify-between items-center p-4 border-b">
            <h3 className="text-xl font-semibold">
              Book Your AI Enabled Waste Solutions Demo
            </h3>
            <button
              onClick={onClose}
              className="text-gray-500 hover:text-gray-700"
            >
              <svg
                className="w-6 h-6"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>
          <div className="p-4">
            <form onSubmit={handleSubmit}>
              <div className="flex flex-col md:flex-row">
                <div className="w-full md:w-1/2 pr-0 md:pr-4 mb-4 md:mb-0">
                  {/* Date Field */}
                  <div className="mb-4">
                    <label className="block font-bold mb-2">
                      Select a Date
                    </label>
                    <input
                      type="datetime-local"
                      name="date"
                      className="w-full p-2 border rounded"
                      value={formData.date}
                      onChange={handleChange}
                      required
                      min={new Date().toISOString().split("T")[0]}
                    />
                  </div>
                  {/* Name Field */}
                  <div className="mb-4">
                    <label className="block font-bold mb-2">Name</label>
                    <input
                      type="text"
                      name="name"
                      className="w-full p-2 border rounded"
                      value={formData.name}
                      onChange={handleChange}
                      required
                    />
                  </div>

                  {/* Email Field */}
                  <div className="mb-4">
                    <label className="block font-bold mb-2">Email</label>
                    <input
                      type="email"
                      name="email"
                      className="w-full p-2 border rounded"
                      value={formData.email}
                      onChange={handleChange}
                      required
                    />
                  </div>

                  {/* Mobile Number Field */}
                  <div className="mb-4">
                    <label className="block font-bold mb-2">
                      Mobile Number
                    </label>
                    <input
                      type="tel"
                      name="mobileNo"
                      className="w-full p-2 border rounded"
                      value={formData.mobileNo}
                      onChange={handleChange}
                      required
                      pattern="[0-9]{10}"
                      title="Please enter a 10-digit mobile number"
                    />
                  </div>
                </div>

                <div className="w-full md:w-1/2 pl-0 md:pl-4">
                  <h6 className="font-bold mb-3">Select Features to Demo</h6>
                  <ul className="space-y-2 text-sm">
                    <li className="flex items-start">
                      <input
                        type="checkbox"
                        name="doorToDoor"
                        checked={formData.features.doorToDoor}
                        onChange={handleChange}
                        className="h-5 w-5 text-green-500 mr-2 mt-0.5"
                      />
                      <span>
                        <strong>Door-to-Door Collection:</strong> Route
                        planning, real-time alerts, and attendance tracking.
                      </span>
                    </li>
                    <li className="flex items-start">
                      <input
                        type="checkbox"
                        name="binMonitoring"
                        checked={formData.features.binMonitoring}
                        onChange={handleChange}
                        className="h-5 w-5 text-green-500 mr-2 mt-0.5"
                      />
                      <span>
                        <strong>Bin Monitoring:</strong> IoT smart bins with
                        fill-level alerts and heat detection.
                      </span>
                    </li>
                    <li className="flex items-start">
                      <input
                        type="checkbox"
                        name="weighbridge"
                        checked={formData.features.weighbridge}
                        onChange={handleChange}
                        className="h-5 w-5 text-green-500 mr-2 mt-0.5"
                      />
                      <span>
                        <strong>Weighbridge Integration:</strong> Auto weight
                        capture and reconciliation.
                      </span>
                    </li>
                    <li className="flex items-start">
                      <input
                        type="checkbox"
                        name="vehicleTracking"
                        checked={formData.features.vehicleTracking}
                        onChange={handleChange}
                        className="h-5 w-5 text-green-500 mr-2 mt-0.5"
                      />
                      <span>
                        <strong>Vehicle Tracking:</strong> GPS, fuel tracking,
                        route deviation alerts.
                      </span>
                    </li>
                    <li className="flex items-start">
                      <input
                        type="checkbox"
                        name="grievance"
                        checked={formData.features.grievance}
                        onChange={handleChange}
                        className="h-5 w-5 text-green-500 mr-2 mt-0.5"
                      />
                      <span>
                        <strong>Grievance Management:</strong> Citizen app,
                        auto-ticketing, SLA monitoring.
                      </span>
                    </li>
                    <li className="flex items-start">
                      <input
                        type="checkbox"
                        name="analytics"
                        checked={formData.features.analytics}
                        onChange={handleChange}
                        className="h-5 w-5 text-green-500 mr-2 mt-0.5"
                      />
                      <span>
                        <strong>Analytics & Dashboards:</strong> Real-time
                        operational insights and reports.
                      </span>
                    </li>
                    <li className="flex items-start">
                      <input
                        type="checkbox"
                        name="customModules"
                        checked={formData.features.customModules}
                        onChange={handleChange}
                        className="h-5 w-5 text-green-500 mr-2 mt-0.5"
                      />
                      <span>
                        <strong>Custom Modules:</strong> Add-ons like carbon
                        tracking, recyclable marketplace.
                      </span>
                    </li>
                  </ul>
                  <button
                    type="submit"
                    className="mt-4 bg-green-600 hover:bg-green-700 text-white py-2 px-4 rounded w-full"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? "Booking..." : "Book Demo"}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>
    </>
  );
};


const Navbar = () => {
  // Authenticating user
  const { isAuthenticated, loginWithRedirect, logout, user } = useAuth0();
  const { isEmpty, totalItems } = useCart();
  const [hoveredItem, setHoveredItem] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchOpen, setSearchOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [showDemoModal, setShowDemoModal] = useState(false);
  const [showProfileDropdown, setShowProfileDropdown] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const cartCount = useCartCount();

  useEffect(() => {
    const checkAuthStatus = () => {
      const authCookie = getCookie("auth");
      setIsLoggedIn(!!authCookie || isAuthenticated);
    };

    checkAuthStatus();
  }, [isAuthenticated]);

  //Searching
  const searchOptions = [
    {
      display: "Shop",
      terms: ["shop", "swachh-shop", "swachhajvshop"],
      path: "/shop",
    },
    {
      display: "Door-to-Door Collection",
      terms: ["door-to-door", "door to door", "collection", "waste collection"],
      path: "/door-to-door-collection",
    },
    {
      display: "Weighbridge System",
      terms: ["weighbridge", "weight bridge", "truck weighing"],
      path: "/weighbridge",
    },
    {
      display: "Bin Monitoring",
      terms: ["bin monitoring", "bin sensor", "fill level", "smart bin", "bin"],
      path: "/bin-monitoring",
    },
    {
      display: "Vehicle Monitoring",
      terms: [
        "vehicle monitoring",
        "vehicle",
        "truck",
        "gps",
        "vehicle management",
      ],
      path: "/vehicle-monitoring",
    },
    {
      display: "Employee Management",
      terms: [
        "employee monitoring",
        "employee",
        "employee management",
        "employee-management",
      ],
      path: "/employee-management",
    },
    {
      display: "AI Waste Management",
      terms: [
        "ai waste management",
        "ai waste",
        "artificial intelligence",
        "ai",
      ],
      path: "/ai-waste-management",
    },
    {
      display: "Story of Waste",
      terms: ["Story of Waste", "story", "story-of-waste"],
      path: "/story-of-waste",
    },
    {
      display: "Vision & Mission",
      terms: ["Vision & Mission", "Vision", "Mission"],
      path: "/vision-mission",
    },

    {
      display: "What is SwachhAJV ?",
      terms: ["solution", "solutions", "waste solution"],
      path: "/swachhajv",
    },
    {
      display: "Case Studies",
      terms: [
        "case study",
        "case-study",
        "case-studies",
        "case",
        "study",
        "studies",
      ],
      path: "/case-studies",
    },
    {
      display: "Datasheet",
      terms: ["datasheet", "data-sheet", "sheet", "datasheets"],
      path: "/datasheet",
    },
    {
      display: "Videos",
      terms: ["videos", "video"],
      path: "/videos",
    },
    {
      display: "Brochure",
      terms: ["Brochure", "Brochures"],
      path: "/brochure",
    },
    {
      display: "Gallery",
      terms: ["Gallery"],
      path: "/gallery",
    },
    {
      display: "Blogs",
      terms: ["Blogs", "blog"],
      path: "/blogs",
    },
    {
      display: "Podcast",
      terms: ["Podcast", "podcast"],
      path: "/podcast",
    },
  ];

  const handleSearch = (query) => {
    const normalizedQuery = query.toLowerCase().trim();

    // Find the matching route (case-insensitive)
    for (const option of searchOptions) {
      const hasMatch = option.terms.some((term) =>
        normalizedQuery.includes(term.toLowerCase())
      );

      if (hasMatch) {
        navigate(option.path);
        setSearchOpen(false);
        setSearchQuery("");
        return;
      }
    }

    alert("No matching component found for your search");
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      handleSearch(searchQuery);
    }
  };
  // Scroll to top function
  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };
  // Scroll to top when route changes
  useEffect(() => {
    scrollToTop();
  }, [location.pathname]);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <>
      <div className="fixed top-0 left-0 w-full z-[9999]">
        {/* Contact info bar with search icon */}
        <div className="hidden md:block bg-white text-black text-sm">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between items-center py-1">
              {/* Left Section */}
              <div className="flex space-x-4">
                <button
                  onClick={() => setShowDemoModal(true)}
                  className="flex items-start hover:text-green-600"
                >
                  <FaRegCalendarAlt
                    className="mr-1.5 h-4 w-4"
                    style={{ color: "#00D084" }}
                  />
                  Schedule Smart Waste Demo
                </button>
                <a
                  href="tel:+919654323500"
                  className="flex items-center hover:text-green-600"
                >
                  <FaPhoneAlt
                    className="mr-1.5 h-4 w-4"
                    style={{ color: "#00D084" }}
                  />
                  +91-9654323500
                </a>
                <a
                  href="mailto:presales@ajeevi.com"
                  className="flex items-center hover:text-green-600"
                >
                  <FaEnvelope
                    className="mr-1.5 h-4 w-4"
                    style={{ color: "#00D084" }}
                  />
                  presales@ajeevi.com
                </a>
              </div>

              {/* Right Section */}
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => setSearchOpen(true)}
                  className="p-2 rounded-xl border border-green-600 text-green-600 hover:bg-green-50 cursor-pointer"
                >
                  <FaSearch className="h-5 w-5" />
                </button>

                <Link
                  to="/signin"
                  className="flex items-center gap-x-1.5 p-2 rounded-xl border border-green-600 text-green-600 hover:bg-green-50 cursor-pointer"
                >
                  <FaSignInAlt className="h-5 w-5" />
                  <span>Sign In</span>
                </Link>
              </div>
            </div>
          </div>
        </div>

        {/* Full-screen search modal */}
        {searchOpen && (
          <div className="fixed inset-0 bg-black bg-opacity-90 z-50 flex items-center justify-center">
            <div className="w-full max-w-3xl px-4">
              <div className="relative">
                <input
                  type="text"
                  placeholder="Search and press Enter"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyPress={handleKeyPress}
                  className="w-full bg-transparent border-b-2 border-white text-white text-3xl py-4 px-2 focus:outline-none placeholder-gray-400"
                  autoFocus
                />
                <button
                  onClick={() => setSearchOpen(false)}
                  className="absolute right-2 top-1/2 transform -translate-y-1/2 text-white hover:text-green-300"
                >
                  <FaTimes className="h-8 w-8" />
                </button>
              </div>

              {/* Suggested search terms with friendly display names */}
              <div className="mt-12 text-center text-white">
                <h3 className="text-xl mb-6">What are you looking for?</h3>
                <div className="flex flex-wrap justify-center gap-4">
                  {searchOptions.map((option) => (
                    <button
                      key={option.path}
                      onClick={() => {
                        setSearchQuery(option.display);
                        handleSearch(option.terms[0]); // Use the first term for searching
                      }}
                      className="px-6 py-2 border border-white rounded-full hover:bg-white hover:text-black transition-colors"
                    >
                      {option.display}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        <Disclosure
          as="nav"
          className={`transition-all duration-300 fixed w-full z-50 ${
            isScrolled ? "bg-white shadow-md" : "bg-transparent"
          }`}
        >
          {({ open, close }) => (
            <>
              <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
                <div className="flex h-16 items-center">
                  <div className="w-full flex items-center justify-between">
                    <div className="flex-shrink-0">
                      <Link to="/">
                        <div
                          className={`font-[1000] font-[Helvetica] text-xl ${
                            isScrolled ? "text-[#00D084]" : "text-[#00D084]"
                          }`}
                        >
                          <p className="flex ml-2">
                            <img
                              src={logo}
                              alt="SwachhAJV brand logo"
                              className="ml-1 mr-2 w-16"
                            />
                            <span className="mt-4 text-2xl">SwachhAJV</span>
                          </p>
                        </div>
                      </Link>
                    </div>
                    <div className="hidden md:block">
                      <div className="ml-10 flex items-baseline space-x-4 mr-2">
                        {navigation.map((item) => (
                          <div
                            key={item.name}
                            className="relative"
                            onMouseEnter={() =>
                              item.subItems && setHoveredItem(item.name)
                            }
                            onMouseLeave={() =>
                              item.subItems && setHoveredItem(null)
                            }
                          >
                            {item.subItems ? (
                              <>
                                <div
                                  className={`flex items-center hover:bg-green-500 rounded-md px-3 py-2 text-sm font-medium ${
                                    isScrolled
                                      ? "text-black hover:text-white"
                                      : "text-white"
                                  }`}
                                >
                                  <span className="text-lg">{item.name}</span>
                                  <ChevronDownIcon
                                    className={`ml-1 h-4 w-4 transition-transform ${
                                      hoveredItem === item.name
                                        ? "transform rotate-180"
                                        : ""
                                    } ${
                                      isScrolled ? "text-black" : "text-white"
                                    }`}
                                    aria-hidden="true"
                                  />
                                </div>
                                {hoveredItem === item.name && (
                                  <div className="absolute left-0 mt-0 w-48 bg-white rounded-md shadow-lg z-10">
                                    <div className="py-1">
                                      {item.subItems.map((subItem) => (
                                        <Link
                                          key={subItem.name}
                                          to={subItem.href}
                                          className="block px-4 py-2 text-md text-gray-900 hover:bg-green-200"
                                        >
                                          {subItem.name}
                                        </Link>
                                      ))}
                                    </div>
                                  </div>
                                )}
                              </>
                            ) : (
                              <Link
                                to={item.href}
                                onClick={scrollToTop}
                                className={`hover:bg-green-500 rounded-md px-3 py-2 text-sm font-medium ${
                                  isScrolled
                                    ? "text-black hover:text-white"
                                    : "text-white"
                                }`}
                              >
                                <span className="text-lg">{item.name}</span>
                              </Link>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                  <div className="md:block">
                    <div className="flex md:ml-6 space-x-2">
                      {/* Cart Icon */}
                      <button
                        type="button"
                        className={`relative rounded-full border-2 p-1 ${
                          isScrolled
                            ? "bg-white text-gray-800 border-green-500"
                            : "bg-green-900 text-white border-green-500"
                        } hover:text-green-500 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 focus:ring-offset-green-500`}
                      >
                        <Link to={cartNavigation[0].href}>
                          <HiOutlineShoppingCart
                            className="h-6 w-6"
                            aria-hidden="true"
                          />
                          {cartCount > 0 && (
                            <span
                              id="cartCount"
                              className="absolute -top-2 -right-2 bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center"
                            >
                              {cartCount}
                            </span>
                          )}
                        </Link>
                      </button>

                      {/* Profile Icon with Dropdown */}
                      <div className="relative">
                        <button
                          type="button"
                          onClick={() =>
                            setShowProfileDropdown(!showProfileDropdown)
                          }
                          className={`hidden md:block relative rounded-full border-2 p-1 ${
                            isScrolled
                              ? "bg-white text-gray-800 border-green-500"
                              : "bg-green-900 text-white border-green-500"
                          } ${
                            isLoggedIn ? "text-green-500" : "text-gray-500"
                          } hover:text-green-500 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 focus:ring-offset-green-500`}
                        >
                          <FaUser className="h-6 w-6" aria-hidden="true" />
                        </button>

                        {showProfileDropdown && (
                          <div
                            className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg z-10"
                            onMouseLeave={() => setShowProfileDropdown(false)}
                          >
                            <div className="py-1">
                              {isLoggedIn ? (
                                <>
                                  <div className="px-4 py-2 text-sm text-gray-700 border-b">
                                    <p className="font-medium">Welcome</p>
                                    <p className="truncate">
                                      {user?.email || "User"}
                                    </p>
                                  </div>
                                  {profileNavigation.subItems.map((item) => (
                                    <Link
                                      key={item.name}
                                      to={item.href}
                                      onClick={() =>
                                        setShowProfileDropdown(false)
                                      }
                                      className="block px-4 py-2 text-md text-black hover:bg-green-100"
                                    >
                                      {item.name}
                                    </Link>
                                  ))}
                                  {/* <button
                                    onClick={() => {
                                      logout({ returnTo: window.location.origin });
                                      setShowProfileDropdown(false);
                                    }}
                                    className="block w-full text-left px-4 py-2 text-md text-black hover:bg-green-100"
                                  >
                                    Logout
                                  </button> */}
                                  <button
                                    onClick={() => {
                                      // Clear cookies
                                      document.cookie =
                                        "auth=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;";
                                      document.cookie =
                                        "userName=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;";
                                      document.cookie =
                                        "email=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;";
                                      document.cookie =
                                        "userId=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;";
                                      document.cookie =
                                        "isLoggedIn=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;";

                                      // Auth0 logout
                                      logout({ localOnly: true });

                                      // Update UI
                                      setShowProfileDropdown(false);
                                      setIsLoggedIn(false);
                                    }}
                                    className="block w-full text-left px-4 py-2 text-md text-black hover:bg-green-100"
                                  >
                                    Logout
                                  </button>
                                </>
                              ) : (
                                <button
                                  onClick={() => {
                                    navigate("/signin");
                                    setShowProfileDropdown(false);
                                  }}
                                  className="block w-full text-left px-4 py-2 text-md text-black hover:bg-green-100"
                                >
                                  Login / Register
                                </button>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                  {/* Mobile */}
                  <div className="-mr-2 flex md:hidden">
                    <Disclosure.Button
                      className={`ml-3 relative inline-flex items-center justify-center rounded-md p-2 ${
                        isScrolled
                          ? "bg-white text-gray-800"
                          : "bg-green-400 text-orange-100"
                      } hover:bg-green-400 hover:text-white focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-gray-800`}
                    >
                      <span className="absolute -inset-0.5" />
                      <span className="sr-only">Menu</span>
                      {open ? (
                        <XMarkIcon
                          className="block h-6 w-6"
                          aria-hidden="true"
                        />
                      ) : (
                        <Bars3Icon
                          className="block h-6 w-6"
                          aria-hidden="true"
                        />
                      )}
                    </Disclosure.Button>
                  </div>
                </div>
              </div>

              <Disclosure.Panel className="md:hidden bg-white">
                <div className="space-y-1 px-2 pb-3 pt-2 sm:px-3">
                  {navigation.map((item) => (
                    <div key={item.name}>
                      {item.subItems ? (
                        <Disclosure>
                          {({ open: isSubMenuOpen }) => (
                            <>
                              <Disclosure.Button
                                className={classNames(
                                  item.current
                                    ? "bg-gray-900 text-white"
                                    : "text-gray-800 hover:bg-green-700 hover:text-white",
                                  "flex justify-between w-full rounded-md px-3 py-2 text-base font-medium"
                                )}
                              >
                                <span>{item.name}</span>
                                <ChevronDownIcon
                                  className={`${
                                    isSubMenuOpen ? "transform rotate-180" : ""
                                  } w-5 h-5 text-gray-800`}
                                  aria-hidden="true"
                                />
                              </Disclosure.Button>
                              <Disclosure.Panel className="pl-4 rounded-md bg-green-200 ml-3 mr-3">
                                {item.subItems.map((subItem) => (
                                  <Link
                                    key={subItem.name}
                                    to={subItem.href}
                                    onClick={() => close()}
                                    className="block rounded-md px-3 py-2 text-base font-medium text-gray-800 hover:bg-green-700 hover:text-white"
                                  >
                                    {subItem.name}
                                  </Link>
                                ))}
                              </Disclosure.Panel>
                            </>
                          )}
                        </Disclosure>
                      ) : (
                        <Link
                          to={item.href}
                          onClick={() => close()}
                          className={classNames(
                            item.current
                              ? "bg-green-900 text-white"
                              : "text-gray-800 hover:bg-green-700 hover:text-white",
                            "block rounded-md px-3 py-2 text-base font-medium"
                          )}
                          aria-current={item.current ? "page" : undefined}
                        >
                          {item.name}
                        </Link>
                      )}
                    </div>
                  ))}

                  {/* Mobile Profile Section */}
                  <div className="pt-4 pb-2 border-t border-gray-200">
                    {isLoggedIn ? (
                      <>
                        <div className="px-4 py-2 text-sm text-gray-700">
                          <p className="font-medium">Welcome</p>
                          <p className="truncate">{user?.email || "User"}</p>
                        </div>
                        {profileNavigation.subItems.map((item) => (
                          <Link
                            key={item.name}
                            to={item.href}
                            onClick={() => close()}
                            className="block rounded-md px-3 py-2 text-base font-medium text-gray-800 hover:bg-green-700 hover:text-white"
                          >
                            {item.name}
                          </Link>
                        ))}
                        <button
                          onClick={() => {
                            logout({ returnTo: window.location.origin });
                            close();
                          }}
                          className="block w-full text-left rounded-md px-3 py-2 text-base font-medium text-gray-800 hover:bg-green-700 hover:text-white"
                        >
                          Logout
                        </button>
                      </>
                    ) : (
                      <button
                        onClick={() => {
                          navigate("/signin");
                          close();
                        }}
                        className="block w-full text-left rounded-md px-3 py-2 text-base font-medium text-gray-800 hover:bg-green-700 hover:text-white"
                      >
                        Login / Register
                      </button>
                    )}
                  </div>
                </div>
              </Disclosure.Panel>
            </>
          )}
        </Disclosure>

        <header className="bg-white shadow">
          <div className="mx-auto max-w-7xl px-4 py-0 sm:px-6 lg:px-8"></div>
        </header>
      </div>

      {/* Demo Modal */}
      <DemoModal show={showDemoModal} onClose={() => setShowDemoModal(false)} />
    </>
  );
};

export default Navbar;
